import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Devices as DevicesIcon
} from '@mui/icons-material';
import { useIsMobile } from '../../../../hooks/useResponsive';
import { usePermissions } from '../../../../contexts/PermissionContext';
import LocationTypeIcon from '../shared/LocationTypeIcon';
import SiblingNavigator from '../shared/SiblingNavigator';
import LabelManager from '../../../../components/LocationSearch/LabelManager';
import { getLocationId, getLocationName, getLocationTypeId } from '../../utils/locationTransformers';

const LocationDetailHeader = ({
  selectedLocation,
  devices,
  siblingNavigation,
  onAddLocation,
  onEditLocation,
  onDeleteLocation,
  onNavigateSibling,
  onRefresh,
  t
}) => {
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();
  const locationId = getLocationId(selectedLocation);
  const typeId = getLocationTypeId(selectedLocation);
  const name = getLocationName(selectedLocation);

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: { xs: 1.5, sm: 3 } }}>
      {/* Top: Info + actions */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: 0 }
      }}>
        {/* Left side - Location info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 0.5 }}>
            <LocationTypeIcon typeId={typeId} size={isMobile ? 24 : 28} withBackground />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  sx={{ fontWeight: 600, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {name}
                </Typography>
                {!isMobile && (
                  <SiblingNavigator
                    {...siblingNavigation}
                    onNavigate={onNavigateSibling}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={selectedLocation.locationTypeModel?.friendlyName || 'Unknown Type'}
                  size="small"
                  variant="outlined"
                />
                {devices.length > 0 && (
                  <Chip
                    icon={<DevicesIcon />}
                    label={devices.length}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {selectedLocation.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedLocation.description}
            </Typography>
          )}
        </Box>

        {/* Action buttons - row below on mobile, right side on desktop */}
        <Box sx={{
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'flex-end' }
        }}>
          {/* Sibling navigator on mobile - shown inline with actions */}
          {isMobile && (
            <SiblingNavigator
              {...siblingNavigation}
              onNavigate={onNavigateSibling}
            />
          )}

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {hasPermission('locations.create') && (
              <Tooltip title={t ? t('HierarchicalLocationView.add_sublocation_1') : 'Add Sublocation'}>
                <IconButton
                  onClick={() => onAddLocation(selectedLocation)}
                  size={isMobile ? 'medium' : 'medium'}
                  aria-label={t ? t('HierarchicalLocationView.add_sublocation_1') : 'Add Sublocation'}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    minWidth: { xs: 44, sm: 'auto' },
                    minHeight: { xs: 44, sm: 'auto' }
                  }}
                >
                  <AddIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
            )}

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {hasPermission('locations.edit') && (
              <Tooltip title={t ? t('LocationManagement.edit_location') : 'Edit Location'}>
                <IconButton
                  onClick={() => onEditLocation(selectedLocation)}
                  size={isMobile ? 'medium' : 'medium'}
                  aria-label={t ? t('LocationManagement.edit_location') : 'Edit Location'}
                  sx={{
                    bgcolor: 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' },
                    minWidth: { xs: 44, sm: 'auto' },
                    minHeight: { xs: 44, sm: 'auto' }
                  }}
                >
                  <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
            )}

            {hasPermission('locations.delete') && (
              <Tooltip title={t ? t('LocationManagement.delete_location') : 'Delete Location'}>
                <IconButton
                  onClick={() => onDeleteLocation(selectedLocation)}
                  size={isMobile ? 'medium' : 'medium'}
                  aria-label={t ? t('LocationManagement.delete_location') : 'Delete Location'}
                  sx={{
                    color: 'error.main',
                    bgcolor: 'error.lighter',
                    '&:hover': { bgcolor: 'error.light', color: 'white' },
                    minWidth: { xs: 44, sm: 'auto' },
                    minHeight: { xs: 44, sm: 'auto' }
                  }}
                >
                  <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      {/* Label Manager */}
      <Box sx={{ mt: { xs: 1, sm: 2 } }}>
        <LabelManager
          locationId={locationId}
          locationName={selectedLocation.friendlyName}
          onLabelsChange={onRefresh}
        />
      </Box>
    </Paper>
  );
};

export default LocationDetailHeader;
